import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getScoreExplanation, getScoringConfig } from "@/lib/api";
import { useAppStore } from "@/store/appStore";
import { statusMeta } from "@/lib/format";
import { StatusChip } from "@/components/tmo/primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calculator, ArrowRight } from "lucide-react";

const COMPONENT_LABELS = {
  T1_test_recency: "Test Recency", T4_test_depth: "Test Depth", T2_scenario_coverage: "Scenario Coverage", T3_test_outcome: "Test Outcome",
};

export default function HowCalculated() {
  const { explainTarget, closeExplain, scenario } = useAppStore();
  const open = !!explainTarget;
  const isApp = explainTarget?.type === "application";

  const { data: expl } = useQuery({
    queryKey: ["explain", explainTarget?.id, scenario],
    queryFn: () => getScoreExplanation(explainTarget.id, scenario),
    enabled: open && isApp,
  });
  const { data: cfg } = useQuery({ queryKey: ["cfg"], queryFn: getScoringConfig, enabled: open });

  const e = expl?.explanation;
  const provenance = useMemo(
    () => (e?.components ? e.components.flatMap((c) => c.inputs || []).slice(0, 6) : []),
    [e]
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && closeExplain()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="how-calculated-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-cnp-navy">
            <Calculator size={18} /> How is this calculated? — {explainTarget?.title}
          </DialogTitle>
          <DialogDescription className="text-sm text-cnp-textMuted">
            Confidence is the Testing Readiness Index — an evidence index. It is a weighted sum of four
            components (Test Recency 40%, Test Depth 30%, Scenario Coverage 15%, Test Outcome 15%); a component with no
            data is removed and the remaining weights renormalised. Every input traces to a source cell
            in the CenterPoint Phase-2 tracking workbook.
          </DialogDescription>
        </DialogHeader>

        {isApp && e && (
          <div className="mt-2">
            <div className="flex items-center gap-3 mb-3 p-3 rounded-lg" style={{ backgroundColor: statusMeta(e.status).bg }}>
              <span className="text-3xl font-bold tnum" style={{ color: statusMeta(e.status).color }}>
                {e.score == null ? "N/A" : `${e.score}%`}
              </span>
              <StatusChip status={e.status} size="lg" />
              <span className="ml-auto text-xs text-cnp-textMuted">Data coverage {e.data_coverage_pct}%</span>
            </div>
            <table className="w-full text-xs">
              <thead><tr className="text-cnp-textMuted text-left border-b border-cnp-border">
                <th className="py-1.5 font-medium">Component</th><th className="py-1.5 font-medium">Weight</th>
                <th className="py-1.5 font-medium">Raw</th><th className="py-1.5 font-medium">Normalised</th>
                <th className="py-1.5 font-medium">Points</th></tr></thead>
              <tbody>
                {e.components.map((c) => (
                  <tr key={c.id} className="border-b border-cnp-border/60">
                    <td className="py-1.5 font-medium text-cnp-text">{c.label}</td>
                    <td className="py-1.5 tnum">{c.weight}%</td>
                    <td className="py-1.5 tnum text-cnp-textMuted">{String(c.raw_value)}</td>
                    <td className="py-1.5 tnum">{c.normalised_value}</td>
                    <td className="py-1.5 tnum font-semibold">{c.points_contributed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {e.caps_applied?.length > 0 && (
              <div className="mt-3 text-xs">
                <span className="font-semibold text-cnp-red">Caps applied: </span>
                {e.caps_applied.map((c) => <span key={c} className="inline-block bg-cnp-red/10 text-cnp-red rounded px-1.5 py-0.5 mr-1 font-medium">{c.replace(/_/g, " ")}</span>)}
              </div>
            )}
            {provenance.length > 0 && (
              <div className="mt-4">
                <div className="text-[11px] uppercase tracking-wide font-semibold text-cnp-blue mb-1">Input Provenance</div>
                {provenance.map((inp, i) => (
                  <div key={`${inp.field}-${inp.source}-${i}`} className="text-[11px] text-cnp-textMuted flex items-center gap-1"><ArrowRight size={11} /> {inp.field} = <b className="text-cnp-text">{String(inp.value)}</b> · {inp.source} · as of {inp.as_of}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {!isApp && cfg && (
          <div className="mt-2">
            <div className="text-[11px] uppercase tracking-wide font-semibold text-cnp-blue mb-2">Weighted Formula</div>
            <div className="bg-cnp-bg rounded-lg p-3 text-xs font-mono text-cnp-text mb-3">
              Overall = 0.35·Subscriber + 0.30·Technician + 0.15·Executive + 0.20·Shared
            </div>
            <table className="w-full text-xs">
              <thead><tr className="text-cnp-textMuted text-left border-b border-cnp-border"><th className="py-1.5 font-medium">Component</th><th className="py-1.5 font-medium">Weight</th></tr></thead>
              <tbody>
                {Object.entries(cfg.weights || {}).map(([k, v]) => (
                  <tr key={k} className="border-b border-cnp-border/60"><td className="py-1.5">{COMPONENT_LABELS[k] || k}</td><td className="py-1.5 tnum font-semibold">{v}%</td></tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-cnp-textMuted mt-3">Caps: a failed scenario test caps at 69% (Not Ready); no evidence caps at 40%; an open critical defect caps at 89%.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
